import { useState } from 'react';
import { Spin, Alert, Timeline, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as claus from 'src/_shared/services/claus/endpoints';
import Section from '../../console/Section.jsx';

const STATUS_CLASS = {
	successful: 'is-ok',
	failed: 'is-fail',
	cancelled: 'is-fail',
	running: 'is-run',
	pending: 'is-run',
};

const DOT_COLOR = {
	successful: 'green',
	failed: 'red',
	cancelled: 'red',
	running: 'blue',
	pending: 'gray',
};

const dayOrdinal = (n) => {
	const rest = n % 100;
	return ['th', 'st', 'nd', 'rd'][(rest - 20) % 10] || ['th', 'st', 'nd', 'rd'][rest] || 'th';
};

// "July 14th, 2026 - 6:00 PM (EDT)" in the viewer's timezone. CLAUS timestamps are ISO strings;
// anything unparseable falls through verbatim. Exported for reuse by the runs/ tab (RunsList).
export function formatRunTime(value) {
	if (!value) return '';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return value;
	const month = d.toLocaleString(undefined, { month: 'long' });
	const time = d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' });
	const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
		.formatToParts(d)
		.find((p) => p.type === 'timeZoneName')?.value;
	return `${month} ${d.getDate()}${dayOrdinal(d.getDate())}, ${d.getFullYear()} - ${time}${zone ? ` (${zone})` : ''}`;
}

// Manual sync trigger + recent run history for this integration, rendered as a timeline. The history
// (`history`) is loaded by the host editor; "Run sync now" sits on the section-title row per the design.
// `title` defaults to the source-agnostic label; the candidate editor overrides it (see
// IntegrationEditor.jsx) since for candidate integrations this section is really the upstream ATS
// pull, distinct from the new "Sync runs" tab covering the engine's own candidate-processing runs.
export default function SyncSection({ orgId, integrationId, source, history, title = 'Sync activity' }) {
	const { loading, data, error, reload } = history;
	const [syncing, setSyncing] = useState(false);

	const items = data?.items || (Array.isArray(data) ? data : []);

	async function runSync() {
		setSyncing(true);
		try {
			const res = await claus.triggerSync(orgId, integrationId);
			message.success(res?.syncHistoryId ? `Sync started (${res.syncHistoryId})` : 'Sync started');
			await reload();
		} catch (e) {
			message.error(e.message || 'Could not start the sync.');
		} finally {
			setSyncing(false);
		}
	}

	return (
		<Section
			id="sec-sync"
			title={title}
			description="The most recent runs for this integration."
			actions={
				<button type="button" className="js-btn" onClick={runSync} disabled={syncing || source === 'sftp'}>
					<ReloadOutlined spin={syncing} /> Run sync now
				</button>
			}
		>
			{error && <Alert type="error" showIcon message="Could not load sync history" description={error.message} />}

			{loading ? (
				<div className="js-loading js-loading--inline">
					<Spin />
				</div>
			) : items.length === 0 ? (
				<div className="ei-empty-inline">No sync runs yet.</div>
			) : (
				<Timeline style={{ marginTop: 6 }}>
					{items.map((run) => (
						<Timeline.Item key={run.id} color={DOT_COLOR[run.status] || 'gray'}>
							<div className="ei-runrow">
								<span className={`ei-status ${STATUS_CLASS[run.status] || ''}`}>{run.status}</span>
								{typeof run.recordCount === 'number' && (
									<span className="ei-runcount">{run.recordCount.toLocaleString()} records</span>
								)}
								<span className="ei-runtime">{formatRunTime(run.startTime)}</span>
								{run.failureReason ? <span className="ei-runfail">{run.failureReason}</span> : null}
							</div>
						</Timeline.Item>
					))}
				</Timeline>
			)}
		</Section>
	);
}
